<script setup lang="ts">
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import { ref, onMounted } from 'vue'
import HeadquartersDataService from '@/services/HeadquartersDataService'
import { Button } from '@/components/ui/button'

interface Vhq {
  id: number
  name: string
  img: string
  badge_text?: string | null
  sort_order?: number
}

const sortedVhqs = ref<Vhq[]>([])
const originalVhqs = ref<Vhq[]>([])
const loading = ref(false)
const saving = ref(false)
const error = ref('')
const saveMessage = ref('')
const saveSuccess = ref(false)

// Drag and drop state
const draggedIndex = ref<number | null>(null)
const draggedItem = ref<Vhq | null>(null)
const listRef = ref<HTMLDivElement | null>(null)

async function fetchVhqs() {
  loading.value = true
  error.value = ''
  try {
    const response = await HeadquartersDataService.getAdminVhqList()
    if (response.data && response.data.success) {
      sortedVhqs.value = response.data.data || []
      originalVhqs.value = [...sortedVhqs.value]
    } else {
      error.value = 'Nepodarilo sa načítať zoznam VHQ.'
    }
  } catch (err: any) {
    error.value = err?.response?.data?.message || 'Chyba pri načítaní VHQ.'
  } finally {
    loading.value = false
  }
}

function onDragStart(index: number, event: DragEvent) {
  draggedIndex.value = index
  draggedItem.value = sortedVhqs.value[index]
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', String(index))
  }
}

function onDragOver(index: number, event: DragEvent) {
  event.preventDefault()
}

function onDrop(targetIndex: number, event: DragEvent) {
  event.preventDefault()
  const sourceIndex = draggedIndex.value
  if (sourceIndex === null || sourceIndex === targetIndex) return

  const newList = [...sortedVhqs.value]
  const [movedItem] = newList.splice(sourceIndex, 1)
  newList.splice(targetIndex, 0, movedItem)
  sortedVhqs.value = newList

  clearDragState()
}

function onDragEnd() {
  clearDragState()
}

function clearDragState() {
  draggedIndex.value = null
  draggedItem.value = null
}

async function saveOrder() {
  saving.value = true
  saveMessage.value = ''
  saveSuccess.value = false

  try {
    const ids = sortedVhqs.value.map(v => v.id)
    const response = await HeadquartersDataService.updateVhqOrder(ids)
    if (response.data && response.data.success) {
      saveSuccess.value = true
      saveMessage.value = 'Poradie bolo úspešne uložené.'
      originalVhqs.value = [...sortedVhqs.value]
    } else {
      saveMessage.value = response.data?.message || 'Nepodarilo sa uložiť poradie.'
    }
  } catch (err: any) {
    saveMessage.value = err?.response?.data?.message || 'Chyba pri ukladaní poradia.'
  } finally {
    saving.value = false
  }
}

function resetOrder() {
  sortedVhqs.value = [...originalVhqs.value]
  saveMessage.value = ''
}

onMounted(() => {
  fetchVhqs()
})
</script>

<template>
  <div class="p-6">
    <PageBreadcrumb page-title="Správa poradia virtuálnych sídel" />
    <p class="text-gray-600 mb-6 dark:text-gray-400">
      Presuňte položky ťahaním (drag & drop) do požadovaného poradia a potom kliknite na tlačítko "Uložiť poradie".
    </p>

    <div v-if="loading" class="text-gray-500 dark:text-gray-400">Načítavam...</div>
    <div v-else-if="error" class="text-red-500 bg-red-50 p-4 rounded-sm mb-4 dark:bg-red-500/10 dark:text-red-400">{{ error }}</div>

    <div v-else>
      <div
        ref="listRef"
        class="space-y-3"
      >
        <div
          v-for="(vhq, index) in sortedVhqs"
          :key="vhq.id"
          draggable="true"
          :class="[
            'flex items-center gap-4 p-4 bg-white rounded-lg shadow-xs border-l-4 transition-all cursor-grab active:cursor-grabbing dark:bg-gray-900 dark:border-gray-800',
            draggedIndex === index ? 'opacity-50 border-gray-400 dark:border-gray-700' : 'border-brand-500'
          ]"
          @dragstart="onDragStart(index, $event)"
          @dragover.prevent="onDragOver(index, $event)"
          @drop="onDrop(index, $event)"
          @dragend="onDragEnd"
        >
          <div class="flex items-center justify-center size-8 rounded-full bg-gray-100 text-gray-500 font-bold select-none dark:bg-gray-800 dark:text-gray-400">
            {{ index + 1 }}
          </div>
          <img :src="vhq.img" class="size-16 object-cover rounded-sm" :alt="vhq.name" />
          <div class="flex-1">
            <div class="font-semibold text-lg">{{ vhq.name }}</div>
            <div v-if="vhq.badge_text" class="inline-block mt-1 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" >
              {{ vhq.badge_text }}
            </div>
          </div>
          <div class="text-gray-400 select-none">
            <svg xmlns="http://www.w3.org/2000/svg" class="size-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </div>
        </div>
      </div>

      <div class="mt-6 flex gap-4">
        <Button
          @click="saveOrder"
          :disabled="saving"
        >
          {{ saving ? 'Ukladám...' : 'Uložiť poradie' }}
        </Button>

        <Button variant="outline" @click="resetOrder">
          Resetovať na pôvodné
        </Button>
      </div>

      <div v-if="saveMessage" :class="saveSuccess ? 'text-green-600' : 'text-red-600'" class="mt-4 font-medium">
        {{ saveMessage }}
      </div>
    </div>
  </div>
</template>
